import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Tag, Button, FilterResetButton, Row, Col, StatCard, Space, Tooltip, Select, Input, DatePicker, EmptyState } from '../../ui';
import { cn } from '../../ui';
import { Plus, Eye, Pencil, Lock, Calendar, CalendarDays, Users, CheckCircle2, PlayCircle, Send, MapPin, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlDateRange } from '../../hooks/useUrlState';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { usePermission, useHasRole } from '../../store/auth';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';
import { TerritoryChips } from '../../components/TerritoryChips';
import { RoleBadge } from '../../components/RoleBadge';
import { UserLink } from '../../components/EntityLinks';
import { AttachmentViewer } from '../../components/AttachmentPreview';
import { EventFormModal } from './EventFormModal';
import {
  EVENT_STATUSES, EVENT_TYPES, STATUS_TONE, TYPE_LABEL, statusHue, statusLabel, typeMeta,
} from './eventMeta';
import { useFilterPanel, FilterRange, hueOption, type FilterChip } from '../../components/FilterPanel';
import { PALETTE, filterTint } from '../../lib/palette';

const STATUS_COLOR = STATUS_TONE;

/**
 * The meet's type, as the small chip it wears everywhere else.
 *
 * It was grey text under the title, which left the Type filter's coloured dot agreeing with nothing
 * in the rows it produced — and a column of "Retailer meet / Retailer meet / Farmer meet" in one grey
 * is read a row at a time.
 */
function TypeChip({ type }: { type?: string | null }) {
  const m = typeMeta(type);
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-pill border px-1.5 py-px type-caption [&>svg]:size-3 [&>svg]:shrink-0', m.chip)}>
      <m.Icon />{m.label}
    </span>
  );
}
/** Five glyphs wide at any score, so a column of ratings lines up. */
const STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(Math.max(0, 5 - n));



/**
 * Whether this meet's PLAN can still be edited.
 *
 * The server decides (`planEditable`, from PLAN_EDITABLE in event.service.ts) and refuses the write
 * either way; the `??` spells the same rule out for a payload from an older build. A finished meet's
 * title, type, date and venue are what people were invited to and what the attendance, the ratings
 * and the outcome report were all recorded against — so the pencil opens the plan to READ, and the
 * outcome, which is the part still worth writing, is written on the event's own page.
 */
const planEditable = (e: any): boolean => e.planEditable ?? (e.status !== 'COMPLETED' && e.status !== 'CANCELLED');

/**
 * Whether this reader may still CORRECT that frozen plan — the title and the venue, the two fields
 * that describe the meet without deciding anything about it. Super Admin only, and the `??` is a
 * local fallback for an API released before the flag: without it the action silently disappears, and
 * a missing button looks exactly like "you are not allowed".
 */
const planCorrectable = (e: any, isSuperAdmin: boolean): boolean =>
  e.planCorrectable ?? (!planEditable(e) && isSuperAdmin);

export function EventsListPage() {
  const navigate = useNavigate();
  const canCreate = usePermission('EVENT.CREATE');
  const canEdit = usePermission('EVENT.EDIT');
  // Who you ARE, not what you may do — and `usePermission` auto-passes for a Super Admin anyway.
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [type, setType] = useUrlString<string | undefined>('type', undefined);
  const [range, setRange] = useUrlDateRange('range');
  /*
   * THREE FILTERS THE SERVER HAS ALWAYS ANSWERED AND NOTHING EVER ASKED FOR.
   *
   * `GET /events` takes `search` and `territoryId` and always has; the toolbar offered neither, so
   * finding one meet among 500 meant reading the table, and the Territory column could be sorted but
   * not narrowed. Organiser is the same question about the column beside it, answered here rather
   * than on the server because the rows are already in hand.
   */
  const [q, setQ] = useUrlString<string | undefined>('q', undefined);
  const [territoryId, setTerritoryId] = useUrlString<string | undefined>('territory', undefined);
  const [organiser, setOrganiser] = useUrlString<string | undefined>('organiser', undefined);
  const [formOpen, setFormOpen] = useState(false);
  const [editRec, setEditRec] = useState<any | null>(null);
  const from = range[0]?.format('YYYY-MM-DD');
  const to = range[1]?.format('YYYY-MM-DD');
  // Server-side column sort (backend ORDER BY over the full result set). Column keys (code / startAt /
  // status) map 1:1 to the backend whitelist, so no fieldMap is needed.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['events', status, type, from, to, q, territoryId, sort],
    queryFn: () => request<any[]>({
      url: '/events',
      params: { status, type, from, to, search: q || undefined, territoryId, ...(sort ? { sort } : {}) },
    }),
    placeholderData: keepPreviousData,
  });

  // KPI tiles are an absolute overview (upcoming / this-month / completed) — compute them from ALL events,
  // not the status/type/date-filtered table, so a toolbar filter narrows only the list, not the tiles.
  const { data: kpiData } = useQuery({
    queryKey: ['events-kpi'],
    queryFn: () => request<any[]>({ url: '/events', params: {} }),
    placeholderData: keepPreviousData,
  });
  const kpiRows = kpiData ?? [];

  const kpis = useMemo(() => {
    const now = dayjs();
    const monthStart = now.startOf('month'), monthEnd = now.endOf('month');
    let upcoming = 0, thisMonth = 0, completed = 0, attendees = 0;
    let running = 0, invitesPending = 0, onList = 0, awaitingFeedback = 0, overdue = 0;
    for (const e of kpiRows) {
      const start = dayjs(e.startAt);
      if (e.status === 'PLANNED' && start.isAfter(now)) upcoming++;
      // Still PLANNED with its date already gone: the meet either happened and was never recorded,
      // or it silently did not happen. Either way it needs a person, so it is counted and named.
      if (e.status === 'PLANNED' && !start.isAfter(now)) overdue++;
      if (!start.isBefore(monthStart) && !start.isAfter(monthEnd)) thisMonth++; // inclusive: an event at exactly 00:00 on the 1st counts
      if (e.status === 'ONGOING') running++;
      if (e.status === 'COMPLETED') { completed++; attendees += Number(e.attendedCount ?? 0); }
      // Invitations that were never sent are the actionable backlog: people on a list who were
      // never told, on meets that have not happened yet.
      if (e.status === 'PLANNED' || e.status === 'ONGOING') {
        onList += Number(e.assignedCount ?? e.invitedCount ?? 0);
        invitesPending += Number(e.pendingInviteCount ?? 0);
      }
      if (e.feedbackOpen) awaitingFeedback++;
    }
    return { upcoming, thisMonth, completed, attendees, running, invitesPending, onList, awaitingFeedback, overdue, total: kpiRows.length };
  }, [kpiRows]);

  /**
   * A KPI that cannot be acted on is decoration — but a KPI that filters to something OTHER than what
   * it counted is worse, because the two disagree in front of the user.
   *
   * "Upcoming" counts PLANNED events whose date has not passed, so its click has to carry the DATE
   * as well as the status; filtering on status alone also returned the overdue ones and the tile read
   * 1 beside a list of 2. Every tile below applies exactly its own definition, and clicking the
   * active one clears it.
   */
  const today = dayjs().startOf('day');
  const setFilter = (nextStatus?: string, nextFrom?: Dayjs | null) => {
    const already = status === nextStatus && (range[0]?.isSame(nextFrom ?? null, 'day') ?? nextFrom == null);
    setStatus(already ? undefined : nextStatus);
    setRange(already ? [null, null] : [nextFrom ?? null, null]);
  };
  const isActive = (st: string, withFrom?: Dayjs | null) =>
    status === st && (withFrom ? !!range[0]?.isSame(withFrom, 'day') : !range[0]);
  const inr0 = (n: number) => n.toLocaleString('en-IN');

  /*
   * The photo the reader clicked, opened in place.
   *
   * The viewer lives at PAGE level rather than in the cell: it is a fullscreen layer, and one that a
   * table cell owns would be unmounted by the next re-render of that row. It opens straight at the
   * cover — the arrow keys and its own filmstrip walk the rest — because a click on a picture that
   * answers with a menu is not the preview the click asked for.
   */
  const [preview, setPreview] = useState<{ files: string[]; label: string; at: number } | null>(null);

  /**
   * ONE PREDICATE, used by the table and by every facet count.
   *
   * `skip` leaves out a single dimension, which is what a facet needs: the count beside "Completed"
   * must be what picking Completed LEAVES, so every other filter applies and its own does not —
   * otherwise choosing one option zeroes all its siblings and the filter can never be changed, only
   * cleared. Two hand-copied filter chains drift apart; there is deliberately only one here.
   */
  type Dim = 'q' | 'type' | 'status' | 'territory' | 'organiser' | 'when';
  const matches = (e: any, skip?: Dim): boolean => {
    if (skip !== 'q' && q) {
      const hay = `${e.code ?? ''} ${e.title ?? ''} ${e.venue ?? ''}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    if (skip !== 'type' && type && e.type !== type) return false;
    if (skip !== 'status' && status && e.status !== status) return false;
    if (skip !== 'territory' && territoryId && String(e.territoryId ?? '') !== territoryId) return false;
    if (skip !== 'organiser' && organiser && String(e.organiserUserId ?? '') !== organiser) return false;
    if (skip !== 'when') {
      const start = dayjs(e.startAt);
      if (range[0] && start.isBefore(range[0].startOf('day'))) return false;
      if (range[1] && start.isAfter(range[1].endOf('day'))) return false;
    }
    return true;
  };

  /*
   * The rows on screen. The server applies everything it can (and caps at 500), and the same
   * predicate runs over what comes back — idempotent for the dimensions the server already handled,
   * and the only place Organiser is applied at all.
   */
  const rows = useMemo(() => (data ?? []).filter((e: any) => matches(e)), [data, q, type, status, territoryId, organiser, range]);

  /**
   * A facet count states WHAT SELECTING THAT OPTION LEAVES — so it is counted over the whole
   * dataset (`kpiRows`, fetched unfiltered for the tiles), not over the already-narrowed table,
   * which cannot see the rows a different pick would bring back.
   *
   * An option reaching 0 comes off the list — a filter must never offer an empty pick — except the
   * SELECTED one, which is kept at 0 so it can still be cleared from the control.
   */
  const facet = (dim: Dim, valueOf: (e: any) => string | null | undefined) => {
    const n = new Map<string, number>();
    for (const e of kpiRows) {
      if (!matches(e, dim)) continue;
      const v = valueOf(e);
      if (v == null || v === '') continue;
      n.set(String(v), (n.get(String(v)) ?? 0) + 1);
    }
    return n;
  };
  const withCount = (label: string, n: number) => `${label} · ${n}`;
  const keep = (value: string, selected: string | undefined, counts: Map<string, number>) =>
    (counts.get(value) ?? 0) > 0 || selected === value;

  const typeCounts = useMemo(() => facet('type', (e) => e.type), [kpiRows, q, status, territoryId, organiser, range]);
  const statusCounts = useMemo(() => facet('status', (e) => e.status), [kpiRows, q, type, territoryId, organiser, range]);
  const territoryCounts = useMemo(() => facet('territory', (e) => e.territoryId), [kpiRows, q, type, status, organiser, range]);
  const organiserCounts = useMemo(() => facet('organiser', (e) => e.organiserUserId), [kpiRows, q, type, status, territoryId, range]);

  /*
   * Territory and Organiser options are derived from the EVENTS, not from the masters.
   *
   * A picker built off the territory master offers 400 beats, 396 of which hold no meet — and the
   * server matches `territoryId` exactly, so an option like "Maharashtra" would return nothing for
   * every meet placed on a beat inside it. Offering only the areas that actually carry a meet means
   * every option returns rows, which is the same promise the counts make.
   */
  const labelsFrom = (pick: (e: any) => { id?: string | null; label?: string | null; extra?: any } | null) => {
    const m = new Map<string, { label: string; extra?: any }>();
    for (const e of kpiRows) {
      const v = pick(e);
      if (!v?.id) continue;
      if (!m.has(String(v.id))) m.set(String(v.id), { label: v.label || String(v.id), extra: v.extra });
    }
    return m;
  };
  const territoryLabels = useMemo(
    () => labelsFrom((e) => (e.territoryId ? { id: String(e.territoryId), label: e.territoryName } : null)),
    [kpiRows],
  );
  const organiserLabels = useMemo(
    () => labelsFrom((e) => (e.organiserUserId ? { id: String(e.organiserUserId), label: e.organiserName, extra: e.organiserRole } : null)),
    [kpiRows],
  );
  const territoryName = (id: string) => territoryLabels.get(id)?.label ?? `area #${id}`;
  const organiserName = (id: string) => organiserLabels.get(id)?.label ?? `staff #${id}`;

  /*
   * WHY THE TABLE IS EMPTY, in the words of the filters that emptied it.
   *
   * Clicking "Upcoming" when it counts 0 left a bare "No data" under four tiles announcing 34 events
   * this month — the reader's next question is "where is my data", and nothing on the screen answered
   * it. The filters are right and the emptiness is right; what was missing was the sentence saying so
   * and a way back out.
   */
  const filtersOn = !!(status || type || range[0] || range[1] || q || territoryId || organiser);
  const filterWords = [
    q ? `matching "${q}"` : null,
    status ? statusLabel(status).toLowerCase() : null,
    type ? (TYPE_LABEL[type] ?? type).toLowerCase() : null,
    territoryId ? `in ${territoryName(territoryId)}` : null,
    organiser ? `run by ${organiserName(organiser)}` : null,
    range[0] ? `from ${range[0].format('DD MMM YYYY')}` : null,
    range[1] ? `up to ${range[1].format('DD MMM YYYY')}` : null,
  ].filter(Boolean).join(' · ');
  const clearFilters = () => {
    setStatus(undefined); setType(undefined); setRange([null, null]);
    setQ(undefined); setTerritoryId(undefined); setOrganiser(undefined);
  };
  // The planned meets that ARE there, sitting outside the date window this filter drew.
  const strandedPlanned = status === 'PLANNED' && !!range[0] && kpis.overdue > 0;

  /**
   * FILTERS, as a panel rather than a row of controls.
   *
   * Four inline controls was the ceiling, and this list wants seven: past that they wrap onto three
   * lines, every filter gets equal weight, and there is nowhere to say what is currently applied. The
   * panel opens IN PLACE under the toolbar, above the rows it narrows, its sections named after the
   * COLUMNS they act on and hued so one can be found without reading all five.
   */
  const chips: FilterChip[] = ([
    q ? { key: 'q', label: `Matching "${q}"`, onRemove: () => setQ(undefined) } : null,
    type ? { key: 'type', label: `Type: ${TYPE_LABEL[type] ?? type}`, onRemove: () => setType(undefined) } : null,
    range[0] || range[1] ? {
      key: 'when',
      label: `When: ${range[0] ? range[0].format('DD MMM YYYY') : 'any'} \u2013 ${range[1] ? range[1].format('DD MMM YYYY') : 'any'}`,
      onRemove: () => setRange([null, null]),
    } : null,
    territoryId ? { key: 'territory', label: `Area: ${territoryName(territoryId)}`, onRemove: () => setTerritoryId(undefined) } : null,
    organiser ? { key: 'organiser', label: `Organiser: ${organiserName(organiser)}`, onRemove: () => setOrganiser(undefined) } : null,
    status ? { key: 'status', label: `Status: ${statusLabel(status)}`, onRemove: () => setStatus(undefined) } : null,
  ].filter(Boolean)) as FilterChip[];

  const filters = useFilterPanel({
    onClear: clearFilters,
    chips,
    groups: [
      {
        title: 'The meet',
        tone: PALETTE.brand[500],
        icon: <CalendarDays size={13} />,
        fields: [
          {
            label: 'Search',
            control: (
              <Input
                allowClear placeholder="Event no, title or venue" value={q ?? ''}
                onChange={(e: any) => setQ(e.target.value || undefined)}
                style={q ? filterTint(PALETTE.brand[500]) : undefined}
              />
            ),
            hint: 'e.g. EVT/2026/09 or "kharif"',
          },
          {
            label: 'Type',
            control: (
              <Select placeholder="Any type" allowClear showSearch optionFilterProp="title" value={type} onChange={setType}
                style={type ? filterTint(typeMeta(type).hue) : undefined}
                options={EVENT_TYPES
                  .filter((v) => keep(v, type, typeCounts))
                  .map((v) => hueOption(v, withCount(TYPE_LABEL[v], typeCounts.get(v) ?? 0), typeMeta(v).hue))} />
            ),
          },
        ],
      },
      {
        title: 'When',
        tone: PALETTE.sky[500],
        icon: <Calendar size={13} />,
        fields: [
          {
            label: 'Held between',
            span: 2 as const,
            control: (
              <FilterRange
                from={<DatePicker placeholder="Start date" value={range[0]} onChange={(d: any) => setRange([d ?? null, range[1]])} />}
                to={<DatePicker placeholder="End date" value={range[1]} onChange={(d: any) => setRange([range[0], d ?? null])} />}
              />
            ),
            hint: 'Either end can stand alone',
          },
        ],
      },
      {
        title: 'Territory',
        tone: PALETTE.plum[400],
        icon: <MapPin size={13} />,
        fields: [
          {
            label: 'Area',
            control: (
              <Select placeholder="Anywhere" allowClear showSearch optionFilterProp="title" value={territoryId} onChange={setTerritoryId}
                style={territoryId ? filterTint(PALETTE.plum[400]) : undefined}
                /* An identity, not a status, so no dot: one colour repeated down forty areas marks
                   nothing, and the section's own hue already says which filter this is. */
                options={[...territoryLabels.entries()]
                  .filter(([id]) => keep(id, territoryId, territoryCounts))
                  .sort((a, b) => (territoryCounts.get(b[0]) ?? 0) - (territoryCounts.get(a[0]) ?? 0) || a[1].label.localeCompare(b[1].label))
                  .map(([id, v]) => ({ value: id, title: v.label, label: withCount(v.label, territoryCounts.get(id) ?? 0) }))} />
            ),
            hint: 'only the areas that hold a meet',
          },
        ],
      },
      {
        title: 'Organiser',
        tone: PALETTE.harvest[500],
        icon: <Users size={13} />,
        fields: [
          {
            label: 'Run by',
            control: (
              <Select placeholder="Anyone" allowClear showSearch optionFilterProp="title" value={organiser} onChange={setOrganiser}
                style={organiser ? filterTint(PALETTE.harvest[500]) : undefined}
                options={[...organiserLabels.entries()]
                  .filter(([id]) => keep(id, organiser, organiserCounts))
                  .sort((a, b) => (organiserCounts.get(b[0]) ?? 0) - (organiserCounts.get(a[0]) ?? 0) || a[1].label.localeCompare(b[1].label))
                  .map(([id, v]) => ({ value: id, title: v.label, label: withCount(v.label, organiserCounts.get(id) ?? 0) }))} />
            ),
          },
        ],
      },
      {
        title: 'Status',
        tone: PALETTE.earth[500],
        icon: <CheckCircle2 size={13} />,
        fields: [
          {
            label: 'State',
            control: (
              <Select placeholder="Any status" allowClear value={status} onChange={setStatus}
                style={status ? filterTint(statusHue(status)) : undefined}
                options={EVENT_STATUSES
                  .filter((s) => keep(s, status, statusCounts))
                  .map((s) => hueOption(s, withCount(statusLabel(s), statusCounts.get(s) ?? 0), statusHue(s)))} />
            ),
            hint: 'counts are what each pick leaves',
          },
        ],
      },
    ],
  });

  const columns: DataTableColumn<any>[] = [
    {
      key: 'cover', title: '', width: 68, align: 'center' as const,
      /* A meet is a real thing that happened in a hall somewhere; a thumbnail says that faster than a
         photo count does — and it OPENS now, into the meet's whole set of photos with the arrow keys
         walking them. A picture you cannot look at is decoration.

         `stopPropagation` because the row itself navigates to the event: without it a click on the
         photo left the page the photo is on. */
      render: (_: any, e: any) => {
        const files: string[] = e.photoUrls?.length ? e.photoUrls : (e.coverUrl ? [e.coverUrl] : []);
        if (!files.length) {
          return (
            <span className="grid size-10 place-items-center rounded bg-surface-2 text-subtle" title="No photos yet">
              <ImageIcon size={15} />
            </span>
          );
        }
        return (
          <button
            type="button"
            aria-label={`Open ${files.length} photo${files.length === 1 ? '' : 's'} of ${e.title}`}
            title={files.length > 1 ? `${files.length} photos — click to open` : 'Click to open'}
            onClick={(ev) => { ev.stopPropagation(); setPreview({ files, label: `${e.title} — photos`, at: 0 }); }}
            className="relative inline-block rounded transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            <img src={files[0]} alt="" loading="lazy" className="size-10 rounded object-cover" />
            {/* How many more are behind it, so the count is on the thing you click. */}
            {files.length > 1 && (
              <span className="absolute -right-1 -top-1 rounded-full border border-line bg-surface px-1 type-caption-strong leading-tight text-muted shadow-e1">
                +{files.length - 1}
              </span>
            )}
          </button>
        );
      },
    },
    { key: 'code', title: 'Event No', dataIndex: 'code', sortable: true, width: 150, alwaysVisible: true },
    {
      key: 'title', title: 'Title', alwaysVisible: true,
      render: (_: any, e: any) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-content">{e.title}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-subtle">
            <TypeChip type={e.type} />
            {e.cadence && e.cadence !== 'ONE_TIME' && <span>{String(e.cadence).toLowerCase()}</span>}
            {e.venue && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <MapPin size={11} className="shrink-0" /><span className="truncate">{e.venue}</span>
              </span>
            )}
            {/* No photo count here: the cover thumbnail at the head of the row already carries it as
                its "+N" badge, and that is the one you can click. The same number twice in one row
                reads as two facts. */}
          </div>
        </div>
      ),
    },
    {
      key: 'rating', title: 'Rating', width: 150,
      /* How the meet actually landed. Without it a list of completed meets is a list of dates —
         you cannot tell the one people valued from the one nobody could hear at the back. */
      render: (_: any, e: any) => {
        if (e.status !== 'COMPLETED') return <span className="text-subtle">—</span>;
        if (!e.feedbackCount) {
          return (
            <span className="text-xs text-subtle">
              {e.feedbackOpen ? 'awaiting replies' : 'no ratings'}
            </span>
          );
        }
        const tone = e.avgRating >= 4 ? 'text-success-600' : e.avgRating >= 3 ? 'text-warning-600' : 'text-danger-600';
        return (
          <div className="flex flex-col gap-0.5">
            <span className="flex items-baseline gap-1.5">
              <span className={`type-body-strong tabular-nums ${tone}`}>{e.avgRating}</span>
              <span className={`text-xs ${tone}`}>{STARS(Math.round(e.avgRating))}</span>
            </span>
            <span className="type-caption text-subtle">
              {e.feedbackCount} of {e.attendedCount ?? 0} replied{e.feedbackOpen ? ' · open' : ''}
            </span>
          </div>
        );
      },
    },
    { key: 'startAt', title: 'When', dataIndex: 'startAt', sortable: true, width: 180, render: (v: any) => formatIst(v) },
    {
      key: 'territory', title: 'Territory', width: 230,
      /* The chain, in the app's own territory colours — state blue, district cyan, beat green, each
         with its glyph — instead of "Ambegaon (PN-AMB)" as flat grey text that says neither which
         rung it sits on nor which district it is in. */
      render: (_: any, e: any) => <TerritoryChips territoryId={e.territoryId} fallbackName={e.territoryName} />,
    },
    {
      key: 'organiser', title: 'Organiser', width: 200,
      /* WHO ran it, and with what authority. A list of meets mixes Admins, Sales Heads and reps, and
         a column of bare names made them all read the same. */
      render: (_: any, e: any) => (e.organiserName ? (
        <span className="inline-flex min-w-0 items-center gap-1.5" onClick={(ev) => ev.stopPropagation()}>
          <UserLink id={e.organiserUserId} role={e.organiserRole} className="truncate">{e.organiserName}</UserLink>
          <RoleBadge role={e.organiserRole} />
        </span>
      ) : <span className="text-subtle">—</span>),
    },
    {
      key: 'people', title: 'Invited / Attended', width: 150, align: 'right',
      render: (_: any, e: any) => (
        <span><b>{e.invitedCount ?? 0}</b>{e.status === 'COMPLETED' ? <span style={{ color: 'var(--color-success-500)' }}> · {e.attendedCount ?? 0} ✓</span> : null}</span>
      ),
    },
    { key: 'photoCount', title: 'Photos', dataIndex: 'photoCount', width: 80, align: 'right', defaultVisible: false },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120, render: (v: string) => <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{statusLabel(v)}</Tag> },
    {
      key: 'actions', title: 'Actions', width: 120, align: 'right', alwaysVisible: true,
      render: (_: any, e: any) => (
        <div onClick={(ev) => ev.stopPropagation()}>
          <Space wrap size={4}>
            {canEdit && (planEditable(e) ? (
              <Tooltip label="Edit the plan">
                <Button variant="text" icon={<Pencil size={16} />} aria-label="Edit the plan" onClick={() => { setEditRec(e); setFormOpen(true); }} />
              </Tooltip>
            ) : (
              <Tooltip label={planCorrectable(e, isSuperAdmin)
                ? 'The plan is locked — you can still correct the title or the venue.'
                : e.status === 'CANCELLED'
                  ? 'Cancelled — the plan is locked. Open it to read what was planned.'
                  : 'Held — the plan is locked. Open it to read it, or the event to write the outcome.'}>
                <Button
                  variant="text" icon={<Lock size={16} />}
                  aria-label={planCorrectable(e, isSuperAdmin) ? 'Correct the record' : 'Read the plan (locked)'}
                  onClick={() => { setEditRec(e); setFormOpen(true); }}
                />
              </Tooltip>
            ))}
            <Tooltip label="Open"><Button variant="text" icon={<Eye size={16} />} aria-label="Open" onClick={() => navigate(`/events/${e.id}`)} /></Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Events &amp; Meets</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditRec(null); setFormOpen(true); }}>New Event</Button>
          ) : undefined}
        </div>
      </div>
      <Row align="stretch" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <StatCard
            label="Upcoming" value={kpis.upcoming} icon={<Calendar />} tone="brand"
            hint={kpis.overdue
              ? `${kpis.thisMonth} this month · ${kpis.overdue} overdue`
              : kpis.thisMonth ? `${kpis.thisMonth} scheduled this month` : 'Nothing scheduled this month'}
            active={isActive('PLANNED', today)}
            /* A tile counting ZERO is not offered as a control: a click whose only outcome is an
               empty table is not a filter. It stays clickable while it IS the active filter, so a
               slice arrived at from a link can still be turned off here. */
            onClick={kpis.upcoming || isActive('PLANNED', today) ? () => setFilter('PLANNED', today) : undefined}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Running now" value={kpis.running} icon={<PlayCircle />} tone="warning"
            hint={kpis.running ? 'Mark attendance before completing' : 'No meet is in progress'}
            active={isActive('ONGOING')}
            onClick={kpis.running || isActive('ONGOING') ? () => setFilter('ONGOING') : undefined}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Invites to send" value={kpis.invitesPending} icon={<Send />}
            tone={kpis.invitesPending ? 'danger' : 'neutral'}
            hint={kpis.invitesPending
              ? `people on upcoming lists who have not been told (of ${inr0(kpis.onList)})`
              : `all ${inr0(kpis.onList)} on upcoming lists have been invited`}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            label="Completed" value={kpis.completed} icon={<CheckCircle2 />} tone="success"
            hint={`${inr0(kpis.attendees)} attendees${kpis.awaitingFeedback ? ` · ${kpis.awaitingFeedback} collecting feedback` : ''}`}
            active={isActive('COMPLETED')}
            onClick={kpis.completed || isActive('COMPLETED') ? () => setFilter('COMPLETED') : undefined}
          />
        </Col>
      </Row>

      <Card>
        <DataTable
          storageKey="events-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(e: any) => ({ onClick: () => navigate(`/events/${e.id}`), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          emptyText={filtersOn ? (
            <EmptyState
              icon={<CalendarDays className="size-8" />}
              title="No events in this slice"
              description={`Showing ${filterWords}.${strandedPlanned
                ? ` The ${kpis.overdue} planned meet${kpis.overdue === 1 ? '' : 's'} you have ${kpis.overdue === 1 ? 'is' : 'are'} already past ${kpis.overdue === 1 ? 'its' : 'their'} date, so ${kpis.overdue === 1 ? 'it falls' : 'they fall'} outside this window.`
                : ''}`}
              action={(
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {strandedPlanned && (
                    <Button size="sm" variant="primary" onClick={() => setRange([null, null])}>
                      Show all planned meets
                    </Button>
                  )}
                  <FilterResetButton onClick={clearFilters} />
                </div>
              )}
            />
          ) : (
            <EmptyState
              icon={<CalendarDays className="size-8" />}
              title="No meets yet"
              description="Retailer meets, farmer meets and trainings you plan will be listed here."
              action={canCreate
                ? <Button size="sm" variant="primary" icon={<Plus size={16} />} onClick={() => { setEditRec(null); setFormOpen(true); }}>New Event</Button>
                : undefined}
            />
          )}
          /* What is applied stays in the TOOLBAR whether the panel is open or shut — you should
             never have to open the filters to find out what is narrowing the list. */
          toolbarLeft={<>{filters.trigger}{filters.chips}</>}
          toolbarBelow={filters.panel}
        />
      </Card>

      {preview && (
        <AttachmentViewer
          files={preview.files}
          index={preview.at}
          label={preview.label}
          onIndex={(i) => setPreview((p) => (p ? { ...p, at: i } : p))}
          onClose={() => setPreview(null)}
        />
      )}

      <EventFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditRec(null); }} record={editRec} />
    </div>
  );
}
